import { useState, useCallback } from 'react';

import Util from '../util/util';
import { trackEngagementAsync } from '../util/engagementTracker';
import { useErrorState, showSuccess } from '../util/error';
import { t } from '../i18n';

/**
 * Custom hook for managing report comments.
 *
 * Consolidates the comment handling pattern that was duplicated in:
 * - ReportDetails.js
 * - AdminReportDetails.js
 *
 * @param {Object} options - Configuration options
 * @param {string} options.uuid - The report UUID
 * @param {number} options.timestamp - The report timestamp
 * @param {Array|string} options.initialComments - Initial comments from the report
 * @param {boolean} options.isAdmin - Whether the current user is an admin (affects comment format)
 *
 * @returns {Object} Comment state and handlers
 *
 * @example
 * const {
 *   comments,
 *   commentInputText,
 *   setCommentInputText,
 *   postComment,
 *   isSubmitting,
 * } = useComments({
 *   uuid: report.uuid,
 *   timestamp: report.timestamp,
 *   initialComments: report.comments,
 *   isAdmin: true,
 * });
 */
const useComments = ({ uuid, timestamp, initialComments = [], isAdmin = false }) => {
  // Error state
  const { error, hasError, handleError, clearError } = useErrorState({
    context: 'Comments',
    showToasts: true,
  });

  // Normalize initial comments
  const normalizeComments = (comments) => {
    if (!comments || comments === 'No comments yet') {
      return [];
    }
    if (Array.isArray(comments)) {
      return comments;
    }
    return [];
  };

  const [comments, setComments] = useState(() => normalizeComments(initialComments));
  const [commentInputText, setCommentInputText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Add a comment to the report via API
   */
  const addCommentToAPI = useCallback(
    async (commentText) => {
      const isFirstComment = comments.length === 0;

      const requestData = {
        queryParams: {
          uuid: uuid,
          timestamp: timestamp,
          comment: JSON.stringify(commentText),
          IsNew: true,
          reportId: Util.formatUuid(uuid),
          setFirstCommentDate: isFirstComment,
          commentDate: Date.now(),
        },
      };

      try {
        const data = await Util.post('Resty', '/items/addComment', requestData);

        showSuccess(t('reports:ticket.commentAdded'));

        // Track comment engagement for surprise badges (shows toast if badge earned)
        trackEngagementAsync('comment_reply');

        // Update cache to track that this report was modified
        await Util.setReportCache(uuid, 'reportChanged', true);
        await Util.setReportCache(uuid, 'reportCommented', true);

        return data.items;
      } catch (err) {
        handleError(err, { userMessage: t('reports:ticket.commentAddFailed') });
        throw err;
      }
    },
    [uuid, timestamp, comments.length, handleError],
  );

  /**
   * Post a new comment (handles both optimistic update and API call)
   */
  const postComment = useCallback(async () => {
    const trimmedText = commentInputText.trim();
    if (!trimmedText) {
      return false;
    }

    setIsSubmitting(true);
    clearError();

    try {
      // Create the new comment object
      // Admin screen uses plain strings, user screen uses objects with metadata
      const newComment = isAdmin
        ? trimmedText
        : {
            comment: trimmedText,
            commenterIsAdmin: false,
          };

      // Optimistic update - add comment to local state immediately
      setComments((prev) => [...prev, newComment]);

      // Clear input immediately for better UX
      setCommentInputText('');

      // Send to API
      await addCommentToAPI(trimmedText);

      setIsSubmitting(false);
      return true;
    } catch (err) {
      // Revert optimistic update on error
      setComments((prev) => prev.slice(0, -1));
      setIsSubmitting(false);
      return false;
    }
  }, [commentInputText, isAdmin, addCommentToAPI, clearError]);

  /**
   * Check if there are any comments
   */
  const hasComments = comments.length > 0;

  /**
   * Get comment count
   */
  const commentCount = comments.length;

  return {
    // State
    comments,
    commentInputText,
    isSubmitting,
    error,
    hasError,
    hasComments,
    commentCount,

    // Actions
    setComments,
    setCommentInputText,
    postComment,
    clearError,

    // For direct API access if needed
    addCommentToAPI,
  };
};

export default useComments;
